import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { searchMerchants, createMerchant, getRecentMerchants } from '../api/merchants'
import { getLocation } from '../utils/geolocation'
import type { MerchantSearchResult } from '../types'

export interface ShopSelection {
  shopName: string
  selectedMerchantId?: string
  shopLat?: number
  shopLng?: number
  // Extra merchant metadata (only set when creating a new shop)
  shopPhone?: string
  shopAddress?: string
  shopCategory?: string
  shopDescription?: string
}

interface Props {
  value: ShopSelection | null
  onChange: (shop: ShopSelection) => void
  onClear: () => void
}

const PAGE_SIZE = 15

const SHOP_CATEGORIES = [
  'Electronics',
  'Clothing & Apparel',
  'Food & Groceries',
  'Home & Furniture',
  'Beauty & Health',
  'Sports & Outdoors',
  'Books & Stationery',
  'Toys & Kids',
  'Automotive',
  'Other',
] as const

// ── Distance label helper ─────────────────────────────────────────────────────

function distanceLabel(km: number | undefined | null): string | null {
  if (km == null) return null
  if (km < 1) return `${Math.round(km * 1000)}m`
  return `${km.toFixed(1)}km`
}

// ── Shop logo / avatar ────────────────────────────────────────────────────────

function ShopAvatar({ logoUrl, size = 40 }: { logoUrl?: string | null; size?: number }) {
  const [imgError, setImgError] = useState(false)
  const sizeClass = size === 48 ? 'w-12 h-12' : 'w-10 h-10'

  if (logoUrl && !imgError) {
    return (
      <img
        src={logoUrl}
        alt=""
        className={`${sizeClass} rounded-xl object-cover flex-shrink-0`}
        onError={() => setImgError(true)}
      />
    )
  }
  return (
    <div
      className={`${sizeClass} rounded-xl flex items-center justify-center flex-shrink-0 text-lg`}
      style={{ background: 'var(--app-page-bg)' }}
    >
      🏪
    </div>
  )
}

// ── Shop row ──────────────────────────────────────────────────────────────────

function ShopRow({ m, onClick }: { m: MerchantSearchResult; onClick: () => void }) {
  const { t } = useTranslation('worker')
  const dist = distanceLabel(m.distance_km)
  return (
    <button
      className="w-full text-left rounded-xl px-3 py-2.5 active:opacity-70"
      style={{ background: 'var(--app-card-bg)' }}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <ShopAvatar logoUrl={m.logo_url} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
              {m.business_name}
            </p>
            {m.is_crowd_verified && (
              <span className="text-xs flex-shrink-0" style={{ color: '#22c55e' }}>✓</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {(m.address || m.location) && (
              <p className="text-xs opacity-50 truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
                {m.address || m.location}
              </p>
            )}
            {m.shop_category && (
              <span
                className="text-xs px-1.5 py-0.5 rounded-md flex-shrink-0"
                style={{ background: 'var(--app-page-bg)', color: 'var(--tg-theme-text-color)', opacity: 0.6 }}
              >
                {m.shop_category}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {(m.product_count ?? 0) > 0 && (
              <p className="text-xs opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
                {t('shops.productsCount', { count: m.product_count ?? 0 })}
              </p>
            )}
            {dist && (
              <p className="text-xs font-medium" style={{ color: 'var(--tg-theme-link-color)' }}>
                📍 {dist}
              </p>
            )}
            {m.source === 'crowd' && !m.is_crowd_verified && (m.product_count ?? 0) === 0 && !dist && (
              <p className="text-xs opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>{t('shopPicker.addedByCrowd')}</p>
            )}
          </div>
        </div>
        <span className="opacity-30 text-lg" style={{ color: 'var(--tg-theme-text-color)' }}>›</span>
      </div>
    </button>
  )
}

// ── Modal shell ──────────────────────────────────────────────────────────────

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'var(--app-page-bg)' }}>
      {children}
    </div>
  )
}

// ── Create-new view ───────────────────────────────────────────────────────────

function CreateView({
  initialName,
  onConfirm,
  onBack,
}: {
  initialName: string
  onConfirm: (sel: ShopSelection) => Promise<void>
  onBack: () => void
}) {
  const { t } = useTranslation('worker')
  const [name, setName] = useState(initialName)
  const [phone, setPhone] = useState('')
  const [address, setAddress] = useState('')
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')
  const [lat, setLat] = useState<number | null>(null)
  const [lng, setLng] = useState<number | null>(null)
  const [gpsLoading, setGpsLoading] = useState(false)
  const [gpsError, setGpsError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleGps = async () => {
    setGpsLoading(true)
    setGpsError(null)
    try {
      const { latitude, longitude } = await getLocation()
      setLat(latitude)
      setLng(longitude)
    } catch (e: unknown) {
      setGpsError(e instanceof Error ? e.message : 'Failed to get location')
    } finally {
      setGpsLoading(false)
    }
  }

  const inputStyle = {
    background: 'var(--app-card-bg)',
    color: 'var(--tg-theme-text-color)',
    border: '1px solid transparent',
    borderRadius: '12px',
    padding: '10px 14px',
    width: '100%',
    fontSize: '16px',
    outline: 'none',
  }

  const labelStyle = {
    fontSize: '11px',
    fontWeight: 500,
    opacity: 0.5,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: 'var(--tg-theme-text-color)',
    marginBottom: '6px',
    display: 'block',
  }

  return (
    <>
      <div className="flex items-center gap-3 px-4 pt-4 pb-3 flex-shrink-0" style={{ borderBottom: '1px solid var(--app-card-bg)' }}>
        <button className="text-sm font-medium" style={{ color: 'var(--tg-theme-link-color)' }} onClick={onBack}>
          {t('common:back', { ns: 'common' })}
        </button>
        <p className="font-semibold text-base flex-1 text-center" style={{ color: 'var(--tg-theme-text-color)' }}>
          {t('shopPicker.newShopTitle')}
        </p>
        <div className="w-10" />
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-6 space-y-4">
        <div>
          <label style={labelStyle}>{t('createShop.nameLabel')}</label>
          <input style={inputStyle} placeholder={t('createShop.namePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div>
          <label style={labelStyle}>{t('createShop.phoneLabel')}</label>
          <input style={inputStyle} type="tel" placeholder={t('createShop.phonePlaceholder')} value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>{t('createShop.addressLabel')}</label>
          <input style={inputStyle} placeholder={t('createShop.addressPlaceholder')} value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>{t('createShop.categoryLabel')}</label>
          <select style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }} value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">{t('createShop.selectCategory')}</option>
            {SHOP_CATEGORIES.map((c) => (
              <option key={c} value={c}>{t(`shops.categories.${c}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>{t('createShop.descriptionLabel')}</label>
          <textarea style={{ ...inputStyle, minHeight: '72px', resize: 'none' }} placeholder={t('createShop.descriptionPlaceholder')} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>{t('createShop.gpsLabel')}</label>
          <button
            className="w-full py-2.5 rounded-xl text-sm font-medium"
            style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
            onClick={handleGps}
            disabled={gpsLoading}
          >
            {gpsLoading ? t('createShop.gpsLoading') : lat != null ? t('createShop.gpsRecapture') : t('createShop.gpsCapture')}
          </button>
          {lat != null && lng != null && (
            <p className="text-xs text-center mt-1.5 opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
              📍 {lat.toFixed(5)}, {lng.toFixed(5)}
            </p>
          )}
          {gpsError && <p className="text-xs text-center mt-1" style={{ color: '#ef4444' }}>{gpsError}</p>}
        </div>
        <p className="text-xs opacity-40 leading-relaxed" style={{ color: 'var(--tg-theme-text-color)' }}>
          {t('createShop.reviewNote')}
        </p>
      </div>

      <div className="p-4 flex-shrink-0 space-y-2" style={{ borderTop: '1px solid var(--app-card-bg)' }}>
        {submitError && <p className="text-xs text-center" style={{ color: '#ef4444' }}>{submitError}</p>}
        <button
          className="w-full py-3 rounded-2xl font-semibold text-base disabled:opacity-40"
          style={{ background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)' }}
          disabled={!name.trim() || submitting}
          onClick={async () => {
            setSubmitting(true)
            setSubmitError(null)
            try {
              await onConfirm({
                shopName: name.trim(),
                shopLat: lat ?? undefined,
                shopLng: lng ?? undefined,
                shopPhone: phone.trim() || undefined,
                shopAddress: address.trim() || undefined,
                shopCategory: category || undefined,
                shopDescription: description.trim() || undefined,
              })
            } catch (e: unknown) {
              setSubmitError(e instanceof Error ? e.message : 'Failed to create shop')
              setSubmitting(false)
            }
          }}
        >
          {submitting ? t('shopPicker.creating') : t('shopPicker.usingThisShop')}
        </button>
      </div>
    </>
  )
}

// ── Search / list view ────────────────────────────────────────────────────────

function SearchView({
  onSelect,
  onCreateNew,
  onClose,
}: {
  onSelect: (m: MerchantSearchResult) => void
  onCreateNew: (prefill: string) => void
  onClose: () => void
}) {
  const { t } = useTranslation('worker')
  const [query, setQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [items, setItems] = useState<MerchantSearchResult[]>([])
  const [recentItems, setRecentItems] = useState<MerchantSearchResult[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [geoLat, setGeoLat] = useState<number | null>(null)
  const [geoLng, setGeoLng] = useState<number | null>(null)
  const [gpsResolving, setGpsResolving] = useState(true)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(async (q: string, skip: number, append: boolean, lat?: number, lng?: number, cat?: string | null) => {
    if (skip === 0) setLoading(true)
    else setLoadingMore(true)
    try {
      const res = await searchMerchants(q, skip, PAGE_SIZE, lat ?? undefined, lng ?? undefined, cat ?? undefined)
      setTotal(res.total)
      setItems((prev) => (append ? [...prev, ...res.items] : res.items))
    } catch {
      // keep existing items on error
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [])

  const loadRecent = useCallback(async (lat?: number, lng?: number) => {
    try {
      const res = await getRecentMerchants(lat, lng, 5)
      setRecentItems(res.items)
    } catch {
      // ignore
    }
  }, [])

  // On mount: load alphabetical immediately, then request GPS and reload geo-sorted
  useEffect(() => {
    load('', 0, false)
    loadRecent()

    getLocation()
      .then(({ latitude, longitude }) => {
        setGeoLat(latitude)
        setGeoLng(longitude)
        load('', 0, false, latitude, longitude, activeCategory)
        loadRecent(latitude, longitude)
      })
      .catch(() => {
        // GPS unavailable — keep alphabetical
      })
      .finally(() => {
        setGpsResolving(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced search on query/category change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      load(query, 0, false, geoLat ?? undefined, geoLng ?? undefined, activeCategory)
    }, 300)
  }, [query, activeCategory, geoLat, geoLng, load])

  const hasMore = items.length < total
  const hasGeo = geoLat != null && geoLng != null
  const showRecent = !query && recentItems.length > 0

  const listHeader = query
    ? t('shops.shopsMatching', { count: total, query })
    : hasGeo
    ? t('shops.nearby')
    : t('shops.shopsCount', { count: total })

  return (
    <>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3 flex-shrink-0" style={{ borderBottom: '1px solid var(--app-card-bg)' }}>
        <p className="font-semibold text-base flex-1" style={{ color: 'var(--tg-theme-text-color)' }}>
          {t('shopPicker.selectShop')}
        </p>
        <button className="text-2xl leading-none opacity-40" style={{ color: 'var(--tg-theme-text-color)' }} onClick={onClose}>
          ×
        </button>
      </div>

      {/* Search bar */}
      <div className="px-4 pt-2 pb-1 flex-shrink-0">
        <div className="flex items-center gap-2 rounded-xl px-3" style={{ background: 'var(--app-card-bg)' }}>
          <span className="opacity-40">🔍</span>
          <input
            style={{
              background: 'transparent',
              color: 'var(--tg-theme-text-color)',
              border: 'none',
              padding: '10px 0',
              width: '100%',
              fontSize: '16px',
              outline: 'none',
            }}
            placeholder={t('shopPicker.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {query && (
            <button className="text-xl leading-none opacity-40" style={{ color: 'var(--tg-theme-text-color)' }} onClick={() => setQuery('')}>
              ×
            </button>
          )}
        </div>
      </div>

      {/* Category filter chips */}
      <div className="flex-shrink-0 overflow-x-auto pb-2 pt-1">
        <div className="flex gap-2 px-4" style={{ width: 'max-content' }}>
          <button
            className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0"
            style={{
              background: activeCategory == null ? 'var(--tg-theme-button-color)' : 'var(--app-card-bg)',
              color: activeCategory == null ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-text-color)',
            }}
            onClick={() => setActiveCategory(null)}
          >
            {t('common:all', { ns: 'common' })}
          </button>
          {SHOP_CATEGORIES.map((cat) => (
            <button
              key={cat}
              className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0"
              style={{
                background: activeCategory === cat ? 'var(--tg-theme-button-color)' : 'var(--app-card-bg)',
                color: activeCategory === cat ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-text-color)',
              }}
              onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
            >
              {t(`shops.categories.${cat}`)}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">

        {/* Recently used section */}
        {showRecent && (
          <div className="px-4 pt-2 pb-1">
            <p className="text-xs font-semibold mb-2 opacity-50 uppercase tracking-wide" style={{ color: 'var(--tg-theme-text-color)' }}>
              {t('shopPicker.recentlyUsed')}
            </p>
            <div className="space-y-1">
              {recentItems.map((m) => (
                <ShopRow key={m.id} m={m} onClick={() => onSelect(m)} />
              ))}
            </div>
          </div>
        )}

        {/* GPS resolving indicator */}
        {gpsResolving && !query && (
          <p className="px-4 pt-2 pb-0 text-xs" style={{ color: 'var(--tg-theme-link-color)', opacity: 0.75 }}>
            {t('shopPicker.findingNearby')}
          </p>
        )}

        {/* "Nearest to you" header when GPS is available and not searching */}
        {!gpsResolving && hasGeo && !query && (
          <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--tg-theme-link-color)', opacity: 0.8 }}>
            {t('shopPicker.nearestToYou')}
          </p>
        )}

        {/* Section header */}
        {!loading && (gpsResolving || !hasGeo || query) && (
          <p className="px-4 pt-3 pb-1 text-xs opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
            {listHeader}
          </p>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>{t('common:loading_', { ns: 'common' })}</p>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="flex flex-col items-center py-12 gap-2">
            <p className="text-sm opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>{t('shopPicker.noShopsFound')}</p>
          </div>
        )}

        {/* Merchant rows */}
        <div className="px-4 space-y-1 pb-2">
          {items.map((m) => (
            <ShopRow key={m.id} m={m} onClick={() => onSelect(m)} />
          ))}
        </div>

        {/* Load more */}
        {hasMore && (
          <div className="px-4 pb-4 pt-2">
            <button
              className="w-full py-2.5 rounded-xl text-sm font-medium disabled:opacity-40"
              style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
              disabled={loadingMore}
              onClick={() => load(query, items.length, true, geoLat ?? undefined, geoLng ?? undefined, activeCategory)}
            >
              {loadingMore ? t('common:loading_', { ns: 'common' }) : t('common:loadMore', { ns: 'common', count: total - items.length })}
            </button>
          </div>
        )}

        <div className="h-24" />
      </div>

      {/* Sticky footer — create new */}
      <div className="p-4 flex-shrink-0" style={{ borderTop: '1px solid var(--app-card-bg)' }}>
        <button
          className="w-full py-2.5 rounded-xl text-sm font-semibold"
          style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
          onClick={() => onCreateNew(query)}
        >
          {query ? t('shopPicker.createNewWithQuery', { query }) : t('shopPicker.createNew')}
        </button>
      </div>
    </>
  )
}

// ── Main ShopPicker ───────────────────────────────────────────────────────────

export default function ShopPicker({ value, onChange, onClear }: Props) {
  const { t } = useTranslation('worker')
  const [modalOpen, setModalOpen] = useState(false)
  const [modalView, setModalView] = useState<'search' | 'create'>('search')
  const [createPrefill, setCreatePrefill] = useState('')

  const handleSelect = (m: MerchantSearchResult) => {
    onChange({
      shopName: m.business_name,
      selectedMerchantId: m.id,
      shopLat: m.crowd_latitude != null ? Number(m.crowd_latitude) : undefined,
      shopLng: m.crowd_longitude != null ? Number(m.crowd_longitude) : undefined,
    })
    setModalOpen(false)
    setModalView('search')
  }

  const handleConfirmNew = async (sel: ShopSelection) => {
    const merchant = await createMerchant({
      shop_name: sel.shopName,
      phone: sel.shopPhone,
      address: sel.shopAddress,
      category: sel.shopCategory,
      description: sel.shopDescription,
      latitude: sel.shopLat,
      longitude: sel.shopLng,
    })
    onChange({
      ...sel,
      selectedMerchantId: String(merchant.id),
    })
    setModalOpen(false)
    setModalView('search')
  }

  const handleOpenCreate = (prefill: string) => {
    setCreatePrefill(prefill)
    setModalView('create')
  }

  const handleClose = () => {
    setModalOpen(false)
    setModalView('search')
  }

  const handleClear = () => {
    onClear()
    setModalView('search')
  }

  // ── Selected chip ──────────────────────────────────────────────────────────
  if (value) {
    return (
      <div className="rounded-xl p-3 flex items-start justify-between gap-3" style={{ background: 'var(--app-card-bg)' }}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg" style={{ background: 'var(--app-page-bg)' }}>
            🏪
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
              {value.shopName}
            </p>
            {value.selectedMerchantId ? (
              <p className="text-xs mt-0.5 font-medium" style={{ color: '#22c55e' }}>{t('shopPicker.linkedShop')}</p>
            ) : (
              <p className="text-xs mt-0.5 opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
                {t('shopPicker.newShop')}{value.shopLat != null && ` · 📍 ${value.shopLat.toFixed(4)}`}
              </p>
            )}
          </div>
        </div>
        <button
          className="text-xs px-3 py-1.5 rounded-lg flex-shrink-0"
          style={{ background: 'var(--app-page-bg)', color: 'var(--tg-theme-text-color)', opacity: 0.6 }}
          onClick={handleClear}
        >
          {t('shopPicker.change')}
        </button>
      </div>
    )
  }

  // ── Trigger button ─────────────────────────────────────────────────────────
  return (
    <>
      <button
        className="w-full rounded-xl px-4 py-3 flex items-center gap-3 text-left"
        style={{ background: 'var(--app-card-bg)' }}
        onClick={() => setModalOpen(true)}
      >
        <span className="text-lg opacity-40">🔍</span>
        <span className="text-sm opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
          {t('shopPicker.searchOrCreate')}
        </span>
      </button>

      {modalOpen && (
        <Modal onClose={handleClose}>
          {modalView === 'search' ? (
            <SearchView
              onSelect={handleSelect}
              onCreateNew={handleOpenCreate}
              onClose={handleClose}
            />
          ) : (
            <CreateView
              initialName={createPrefill}
              onConfirm={handleConfirmNew}
              onBack={() => setModalView('search')}
            />
          )}
        </Modal>
      )}
    </>
  )
}
